import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';

import { toast } from 'react-toastify';

import { ImpactPartner } from '../models/ImpactPartner';
import { Order } from '../models/Order';
import ImpactPartnerService from '../services/ImpactPartnerService';
import OrderService from '../services/OrderService';
import * as AuthorizationStore from '../store/Authorization';
import { buildOrdersCsvAndDownload } from '../components/shared/Utils';

export interface IState {
    items: Order[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    loading: boolean;
    postBack: boolean;
    appendSearch: boolean;
    reorderInProgress: boolean;
    reorderCartId: any;
    orderProductsChanged: boolean;
    checkingOrderProduct: boolean;
    isLoadingForCsv: boolean;

}

export const unloadedState: IState = {
    items: [],
    totalCount: -1,
    pageIndex: 0,
    pageSize: 8,
    loading: false,
    postBack: false,
    appendSearch: false,
    reorderInProgress: false,
    reorderCartId: undefined,
    orderProductsChanged: false,
    checkingOrderProduct: true,
    isLoadingForCsv: false
};

export enum Actions {
    OrderSearchBegin = "OrderSearchBegin",
    OrderSearchEnd = "OrderSearchEnd",
    OrderSearchError = "OrderSearchError",
    OrderSearchReset = "OrderSearchReset",
    ReorderBegin = "ReorderBegin",
    ReorderEnd = "ReorderEnd",
    ReorderError = "ReorderError",
    BeginVerifyOrderProducts = "BeginVerifyOrderProducts",
    VerifyOrderProducts = "VerifyOrderProducts",
    OrderSearchAndExportBegin = "OrderSearchAndExportBegin",
    OrderSearchAndExportEnd = "OrderSearchAndExportEnd",
}

interface OrderSearchBeginAction {
    type: Actions.OrderSearchBegin;
    appendSearch: boolean,
}

interface OrderSearchEndAction {
    type: Actions.OrderSearchEnd;
    items: Order[];
    totalCount: number;
    pageIndex: number;
}

interface OrderSearchErrorAction {
    type: Actions.OrderSearchError;
}

interface OrderSearchResetAction {
    type: Actions.OrderSearchReset;
}

interface ReorderBeginAction {
    type: Actions.ReorderBegin;
    inProgress: boolean,
}

interface ReorderEndAction {
    type: Actions.ReorderEnd;
    cartId: any;
}

interface ReorderErrorAction {
    type: Actions.ReorderError;
}

interface BeginVerifyOrderProductsAction {
    type: Actions.BeginVerifyOrderProducts;
}

interface VerifyOrderProductsAction {
    type: Actions.VerifyOrderProducts;
    orderProductsChanged: boolean;
}

interface OrderSearchAndExportBeginAction {
    type: Actions.OrderSearchAndExportBegin;
}

interface OrderSearchAndExportEndAction {
    type: Actions.OrderSearchAndExportEnd;
}

type KnownAction = OrderSearchBeginAction | OrderSearchEndAction | OrderSearchErrorAction | OrderSearchResetAction
    | ReorderBeginAction | ReorderEndAction | ReorderErrorAction | VerifyOrderProductsAction | BeginVerifyOrderProductsAction
    | OrderSearchAndExportBeginAction | OrderSearchAndExportEndAction;

export const actionCreators = {
    orderSearch: (orderPartner: ImpactPartner, orderNumber: string, dateStart: Date, dateEnd: Date, cartName: string, pageIndex: number, pageSize: number, appendSearch: boolean): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.OrderSearchBegin,
                appendSearch,
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const orderPartnerId = orderPartner ? orderPartner.Id : null;
            const response = await OrderService.orderSearch(partner.Id, orderPartnerId, orderNumber, dateStart, dateEnd, cartName, pageIndex, pageSize);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.OrderSearchEnd,
                    items: response.value.Items,
                    totalCount: response.value.TotalItems,
                    pageIndex: pageIndex
                });
            }
            else {
            }
        }
    },
    orderSearchReset: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({
            type: Actions.OrderSearchReset,
        });
    },

    reorder: (orderPartnerId: any, orderId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.ReorderBegin,
                inProgress: true
            });

            var partnerResponse = await ImpactPartnerService.get(orderPartnerId);
            var orderPartner = partnerResponse.value;

            const loggedPartner = AuthorizationStore.getImpactPartnerFromLocalStorage();

            const response = await OrderService.reorder(loggedPartner.Id, state.authorization.currentUser.UserId, orderPartner, orderId);

            if (!response.hasErrors) {
                toast.success("Reorder success.");

                dispatch({
                    type: Actions.ReorderEnd,
                    cartId: response.value
                });
            }
            else {
                toast.error("Error reorder.");
                console.log("Reorder", response.errorMessage);

                dispatch({
                    type: Actions.ReorderEnd,
                    cartId: undefined
                });
            }
        }
    },

    checkOrderProducts: (orderPartnerId: any, orderId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.BeginVerifyOrderProducts
            });

            var partnerResponse = await ImpactPartnerService.get(orderPartnerId);
            var orderPartner = partnerResponse.value;

            const response = await OrderService.checkOrderProducts(orderPartner, orderId);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.VerifyOrderProducts,
                    orderProductsChanged: response.value.isChanged
                });
            }
        }
    },

    loadAllAndDownloadCsv: (orderPartner: ImpactPartner, orderNumber: string, dateStart: Date, dateEnd: Date, cartName: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.OrderSearchAndExportBegin
            });

            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const orderPartnerId = orderPartner ? orderPartner.Id : null;
            const response = await OrderService.orderSearch(partner.Id, orderPartnerId, orderNumber, dateStart, dateEnd, cartName, 0, 10000);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.OrderSearchAndExportEnd
                });

                buildOrdersCsvAndDownload(response.value.Items, "orders-" + new Date().getTime() + ".csv");
            }
            else {
            }
        }
    },
};

export const reducer: Reducer<IState> = (state: IState | undefined, incomingAction: Action): IState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {
        case Actions.OrderSearchBegin: {

            return {
                ...state,
                loading: true,
                postBack: true,
                appendSearch: action.appendSearch,
                items: !action.appendSearch ? [] : state.items,
                totalCount: !action.appendSearch ? -1 : state.totalCount,
            };
        }

        case Actions.OrderSearchEnd: {

            return {
                ...state,
                loading: false,
                totalCount: action.totalCount,
                items: state.appendSearch ? [...state.items, ...action.items] : [...action.items],
                pageIndex: action.pageIndex
            };
        }

        case Actions.OrderSearchError: {

            return {
                ...state,
                loading: false
            };
        }

        case Actions.OrderSearchReset: {
            return unloadedState;
        }

        case Actions.ReorderBegin: {

            return {
                ...state,
                reorderInProgress: true
            };
        }

        case Actions.ReorderEnd: {

            return {
                ...state,
                reorderInProgress: false,
                reorderCartId: action.cartId
            };
        }

        case Actions.ReorderError: {

            return {
                ...state,
                reorderInProgress: false
            };
        }

        case Actions.BeginVerifyOrderProducts: {

            return {
                ...state,
                checkingOrderProduct: true
            };
        }

        case Actions.VerifyOrderProducts: {

            return {
                ...state,
                orderProductsChanged: action.orderProductsChanged,
                checkingOrderProduct: false
            };
        }

        case Actions.OrderSearchAndExportBegin: {

            return {
                ...state,
                isLoadingForCsv: true
            };
        }

        case Actions.OrderSearchAndExportEnd: {

            return {
                ...state,
                isLoadingForCsv: false
            };
        }

        default: return state;
    }
};